import React, { useCallback, useEffect, useRef, useState } from 'react';
import { recordScore, getProgress, setLevel, unlockNext, getHighScore } from '../gamesLib';

interface Props {
  onExit: () => void;
  challengeScore?: number | null;
}

// Clues kept per difficulty level (fewer = harder).
const LEVELS = [
  { name: 'Easy', clues: 40 },
  { name: 'Medium', clues: 32 },
  { name: 'Hard', clues: 28 },
  { name: 'Expert', clues: 24 },
];

type Grid = number[][];

function emptyGrid(): Grid {
  return Array.from({ length: 9 }, () => Array(9).fill(0));
}

function shuffle<T>(a: T[]): T[] {
  const arr = a.slice();
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function canPlace(g: Grid, r: number, c: number, v: number): boolean {
  for (let i = 0; i < 9; i++) {
    if (g[r][i] === v || g[i][c] === v) return false;
  }
  const br = Math.floor(r / 3) * 3, bc = Math.floor(c / 3) * 3;
  for (let i = 0; i < 3; i++) for (let j = 0; j < 3; j++) if (g[br + i][bc + j] === v) return false;
  return true;
}

function fillGrid(g: Grid): boolean {
  for (let r = 0; r < 9; r++) {
    for (let c = 0; c < 9; c++) {
      if (g[r][c] === 0) {
        for (const v of shuffle([1, 2, 3, 4, 5, 6, 7, 8, 9])) {
          if (canPlace(g, r, c, v)) {
            g[r][c] = v;
            if (fillGrid(g)) return true;
            g[r][c] = 0;
          }
        }
        return false;
      }
    }
  }
  return true;
}

function makePuzzle(clues: number): { puzzle: Grid; solution: Grid } {
  const solution = emptyGrid();
  fillGrid(solution);
  const puzzle = solution.map(r => r.slice());
  // Remove cells until only `clues` remain.
  const cells = shuffle(Array.from({ length: 81 }, (_, i) => i));
  let remaining = 81;
  for (const idx of cells) {
    if (remaining <= clues) break;
    const r = Math.floor(idx / 9), c = idx % 9;
    if (puzzle[r][c] !== 0) { puzzle[r][c] = 0; remaining--; }
  }
  return { puzzle, solution };
}

function isComplete(g: Grid): boolean {
  for (let r = 0; r < 9; r++) for (let c = 0; c < 9; c++) {
    const v = g[r][c];
    if (v === 0) return false;
    g[r][c] = 0;
    const ok = canPlace(g, r, c, v);
    g[r][c] = v;
    if (!ok) return false;
  }
  return true;
}

const GameSudoku: React.FC<Props> = ({ onExit, challengeScore }) => {
  const prog = getProgress('sudoku');
  const [level, setLvl] = useState(prog.level || 1);
  const [{ puzzle, solution }, setData] = useState(() => makePuzzle(LEVELS[(prog.level || 1) - 1].clues));
  const [grid, setGrid] = useState<Grid>(() => puzzle.map(r => r.slice()));
  const [sel, setSel] = useState<[number, number] | null>(null);
  const [seconds, setSeconds] = useState(0);
  const [done, setDone] = useState(false);
  const [best] = useState(getHighScore('sudoku'));
  const [score, setScore] = useState(0);
  const fixedRef = useRef<boolean[][]>(puzzle.map(row => row.map(v => v !== 0)));
  const savedRef = useRef(false);

  const reset = useCallback((lvl: number) => {
    const d = makePuzzle(LEVELS[Math.min(lvl - 1, LEVELS.length - 1)].clues);
    setData(d);
    setGrid(d.puzzle.map(r => r.slice()));
    fixedRef.current = d.puzzle.map(row => row.map(v => v !== 0));
    setSel(null);
    setSeconds(0);
    setDone(false);
    setScore(0);
    savedRef.current = false;
    setLvl(lvl);
    setLevel('sudoku', lvl);
  }, []);

  useEffect(() => {
    if (done) return;
    const id = setInterval(() => setSeconds(s => s + 1), 1000);
    return () => clearInterval(id);
  }, [done]);

  const place = (v: number) => {
    if (!sel || done) return;
    const [r, c] = sel;
    if (fixedRef.current[r][c]) return;
    setGrid(g => {
      const ng = g.map(row => row.slice());
      ng[r][c] = ng[r][c] === v ? 0 : v;
      if (isComplete(ng)) setDone(true);
      return ng;
    });
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!sel) return;
      if (e.key >= '1' && e.key <= '9') place(Number(e.key));
      else if (e.key === 'Backspace' || e.key === 'Delete' || e.key === '0') place(grid[sel[0]][sel[1]]);
      else if (e.key.startsWith('Arrow')) {
        e.preventDefault();
        let [r, c] = sel;
        if (e.key === 'ArrowUp') r = Math.max(0, r - 1);
        if (e.key === 'ArrowDown') r = Math.min(8, r + 1);
        if (e.key === 'ArrowLeft') c = Math.max(0, c - 1);
        if (e.key === 'ArrowRight') c = Math.min(8, c + 1);
        setSel([r, c]);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  useEffect(() => {
    if (done && !savedRef.current) {
      savedRef.current = true;
      const lvlBonus = level * 500;
      const timeScore = Math.max(100, 2000 - seconds * 3);
      const final = lvlBonus + timeScore;
      setScore(final);
      recordScore('sudoku', final, level);
      unlockNext('sudoku', level);
    }
  }, [done, seconds, level]);

  const selVal = sel ? grid[sel[0]][sel[1]] : 0;

  return (
    <div className="gsudoku">
      <div className="game-toolbar">
        <button className="game-back" onClick={onExit}>← Arcade</button>
        <div className="game-stats">
          <span className="gstat"><b>{seconds}s</b><i>time</i></span>
          <span className="gstat"><b>{best}</b><i>best</i></span>
          <span className="gstat"><b>{LEVELS[level - 1].name}</b><i>mode</i></span>
        </div>
        <button className="game-restart" onClick={() => reset(level)}>↻ New</button>
      </div>

      <div className="g2048-levels">
        {LEVELS.map((lv, i) => {
          const lvl = i + 1;
          const unlocked = lvl <= getProgress('sudoku').unlockedLevel;
          return (
            <button key={lv.name} className={`lvl-pill ${level === lvl ? 'active' : ''} ${unlocked ? '' : 'locked'}`}
              onClick={() => unlocked && reset(lvl)} disabled={!unlocked}>
              {unlocked ? lv.name : '🔒'}
            </button>
          );
        })}
      </div>

      {challengeScore != null && (
        <div className="challenge-target">🎯 Beat the challenge: <b>{challengeScore}</b> points</div>
      )}

      <div className="gsudoku-board">
        {grid.map((row, r) =>
          row.map((v, c) => {
            const isFixed = fixedRef.current[r][c];
            const isSel = sel && sel[0] === r && sel[1] === c;
            const peer = sel && (sel[0] === r || sel[1] === c || (Math.floor(sel[0] / 3) === Math.floor(r / 3) && Math.floor(sel[1] / 3) === Math.floor(c / 3)));
            const sameVal = v !== 0 && v === selVal;
            const wrong = v !== 0 && !isFixed && v !== solution[r][c];
            return (
              <button
                key={`${r}-${c}`}
                className={`gsud-cell ${isFixed ? 'fixed' : ''} ${isSel ? 'sel' : ''} ${peer && !isSel ? 'peer' : ''} ${sameVal && !isSel ? 'same' : ''} ${wrong ? 'wrong' : ''} ${c % 3 === 2 ? 'br' : ''} ${r % 3 === 2 ? 'bb' : ''}`}
                onClick={() => setSel([r, c])}
              >
                {v || ''}
              </button>
            );
          })
        )}
      </div>

      <div className="gsud-pad">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => (
          <button key={n} className="gsud-num" onClick={() => place(n)} disabled={!sel}>{n}</button>
        ))}
        <button className="gsud-num gsud-erase" onClick={() => sel && place(grid[sel[0]][sel[1]])} disabled={!sel}>⌫</button>
      </div>

      {done && (
        <div className="game-overlay">
          <div className="game-overlay-card">
            <div className="go-emoji">🎉</div>
            <h3>Solved!</h3>
            <p>{LEVELS[level - 1].name} · {seconds}s · <b>{score}</b> points{level < LEVELS.length ? ' · next level unlocked' : ''}</p>
            <div className="go-actions">
              {level < LEVELS.length && (
                <button className="g-btn primary" onClick={() => reset(level + 1)}>Next level →</button>
              )}
              <button className="g-btn" onClick={() => reset(level)}>New puzzle</button>
              <button className="g-btn ghost" onClick={onExit}>Back to arcade</button>
            </div>
          </div>
        </div>
      )}

      <p className="game-hint">Tap a cell, then a number (or use your keyboard). Wrong entries turn red.</p>
    </div>
  );
};

export default GameSudoku;
